import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { PsnbUserRolesPermissionConfigResponse, UserDetails } from '@psnb/psnb-api';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { UserService } from '../data-providers/user-service';
import { isDefinedNotNull, isDefinedNotNullNotEmpty, isUndefinedOrNull } from '../util';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public userDetails: UserDetails;
  public userPermissions: PsnbUserRolesPermissionConfigResponse[];

  public constructor(
    private msalService: MsalService,
    private userService: UserService
  ) {}

  /**
   * Returns true if the user details are stored in the local storage and false otherwise.
   */
  public async checkUserDetails(): Promise<boolean> {
    try {
      await this.fetchUserDetails();
      return true;
    } catch (err: unknown) {
      return false;
    }
  }

  private fetchUserDetails(): Promise<void> {
    return new Promise((resolve: Function, reject: Function): void => {
      this.userService.getUserDetails()
        .pipe(take(1))
        .subscribe({
          next: async (usrDetails: UserDetails): Promise<void> => {
            this.userDetails = usrDetails;
            await this.fetchUserPermissions();
            if (this.isAuthorizedPSNBUser) {
              // If user has application access then keep it in the local storage
              localStorage.setItem('user-details', JSON.stringify(this.userDetails));
            }
            resolve();
          },
          error: (): void => { // eslint-disable-line sort-keys
            reject();
          }
        });
    });
  }

  private async fetchUserPermissions(): Promise<void> {
    this.userPermissions = await this.userService.getUserRolePermissionConfigs();
    localStorage.setItem('user-permissions', JSON.stringify(this.userPermissions));
  }

	public get getUserDetails(): UserDetails {
		return this.userDetails;
	}

	public get isAuthorizedPSNBUser(): boolean {
    return isDefinedNotNull(this.userDetails?.permissions) && this.userDetails.permissions.includes('{1089}');
  }

  /**
   * Checks if the user has OSSEngineer role authorization.
   *
   * @return True if the permission contains the 'OSSEngineer' role, false otherwise.
   */
  public get isAuthorizedOSSEngineer(): boolean {
    const ossRolePermission: PsnbUserRolesPermissionConfigResponse = this.userPermissions?.[0];
    return ossRolePermission?.role.includes('OSSEngineer') ?? false;
  }

  /**
   * Checks if the user is authorized for Power Supply operations.
   * Requires valid user permissions, PSNB user authorization, and OSSEngineer role.
   *
   * @param userPermissions The user permissions array from localStorage
   * @return True if the user has all required authorizations, false otherwise.
   */
  public isAuthorizedForPowerSupply(userPermissions: PsnbUserRolesPermissionConfigResponse[]): boolean {
    return isDefinedNotNullNotEmpty(userPermissions) &&
      this.isAuthorizedPSNBUser ||
      this.isAuthorizedOSSEngineer;
  }

  /*
   * This method is used to get the access token for the user.
   * If it is not available in the cache, it will redirect the user to the login page and then back to the app URL
   *  they were previously viewing.
   */
  public getAccessToken(scopes: string[]): Observable<string> {
    const redirectStartPage: string = window.location.href;

    return this.msalService
      .acquireTokenSilent({
        account: this.msalService.instance.getAllAccounts()[0],
        scopes
      })
      .pipe(
        catchError((authResult: AuthenticationResult): Observable<AuthenticationResult> => {
          this.msalService.getLogger().error(
            'acquireTokenSilent rejected with error. Invoking interaction to resolve.'
          );

          return this.msalService
            .acquireTokenRedirect({
              account: this.msalService.instance.getAllAccounts()[0],
              redirectStartPage,
              scopes
            })
            .pipe(map((): AuthenticationResult => authResult));
        }),
        switchMap((result: AuthenticationResult): Observable<string> => {
          if (isUndefinedOrNull(result?.idToken)) {
            this.msalService.getLogger().error(
              // eslint-disable-next-line max-len
              'acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve.'
            );

            return this.msalService
              .acquireTokenRedirect({
                account: this.msalService.instance.getAllAccounts()[0],
                redirectStartPage,
                scopes
              })
              .pipe(map((): string => ''));
          }
          return of(result.idToken);
        }),
        take(1)
      );
  }
}
